import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import communicationApi, { SavedRef } from '../../../../services/communicationApi';
import { MessageComposition, ProviderTemplate, Template, emptyChannelConfig } from '../../../../types/communication';
import { isEnvelope, templateInput, unusedVariableNames, usedConstantNames, withSyncedVariables } from '../composition/studio/studioDraft';
import { TemplateKind, newTemplateOfKind } from './templateKinds';

export type TemplateEditorMode = 'new' | 'view' | 'edit';

const errorMessage = (e: unknown, fallback: string): string => (e instanceof Error && e.message ? e.message : fallback);

const sync = (template: Template): Template => withSyncedVariables(template, emptyChannelConfig(template.channel));

const SES_BODY_UNAVAILABLE =
    'Não foi possível ler o corpo do e-mail no SES. Recarregue a tela antes de salvar para não gravar um corpo vazio.';

export const useTemplateEditor = (id: string | undefined, mode: TemplateEditorMode) => {
    const [template, setTemplate] = useState<Template | null>(null);
    const [compositions, setCompositions] = useState<MessageComposition[]>([]);
    const [allTemplates, setAllTemplates] = useState<Template[]>([]);
    const [apiVersion, setApiVersion] = useState('');
    const [providerCatalog, setProviderCatalog] = useState<ProviderTemplate[]>([]);
    const [loading, setLoading] = useState(mode !== 'new');
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const load = useCallback(async () => {
        const [compositionPage, templatePage, catalog] = await Promise.all([
            communicationApi.listMessageCompositions({ pageSize: 9999 }),
            communicationApi.listTemplates({ pageSize: 9999 }),
            communicationApi.getProviderCatalog('BLIP').catch(() => [] as ProviderTemplate[]),
        ]);
        setCompositions(compositionPage.list);
        setAllTemplates(templatePage.list);
        setApiVersion(`${templatePage.apiName} - ${templatePage.apiVersion}`);
        setProviderCatalog(catalog);
        if (!id || mode === 'new') {
            setTemplate(null);
            return;
        }
        const loaded = await communicationApi.getTemplate(id);
        if (loaded.contentUnavailable) toast.warn(SES_BODY_UNAVAILABLE);
        setTemplate(sync(loaded));
        setDirty(false);
    }, [id, mode]);

    useEffect(() => {
        setLoading(true);
        load()
            .catch(() => toast.error('Modelo não encontrado.'))
            .finally(() => setLoading(false));
    }, [load]);

    const composition = template ? compositions.find((c) => c.id === template.messageCompositionId) ?? null : null;
    const linked = Boolean(composition);
    const readOnly = mode === 'view' || linked;

    const start = (kind: TemplateKind) => {
        setTemplate(sync(newTemplateOfKind(kind)));
        setDirty(true);
    };

    const update = (patch: Partial<Template>) => {
        if (!template || readOnly) return;
        setTemplate(sync({ ...template, ...patch }));
        setDirty(true);
    };

    const save = async (): Promise<SavedRef | null> => {
        if (!template) return null;
        if (template.contentUnavailable) {
            toast.error(SES_BODY_UNAVAILABLE);
            return null;
        }
        if (!template.name.trim()) {
            toast.warn('Dê um nome ao modelo.');
            return null;
        }
        if (template.variables.some((v) => !v.label.trim())) {
            toast.warn('Toda variável precisa de um rótulo.');
            return null;
        }
        setSaving(true);
        try {
            const saved =
                mode === 'new'
                    ? await communicationApi.createTemplate(templateInput(template))
                    : await communicationApi.reviseFreeTemplate(template.id, templateInput(template));
            setDirty(false);
            toast.success(mode === 'new' ? 'Modelo criado.' : 'Modelo salvo.');
            return saved;
        } catch (e) {
            toast.error(errorMessage(e, 'Falha ao salvar o modelo.'));
            return null;
        } finally {
            setSaving(false);
        }
    };

    const duplicate = async (): Promise<Template | null> => {
        if (!template) return null;
        try {
            const copy = await communicationApi.duplicateTemplate(template.id);
            toast.success('Modelo duplicado. A cópia está livre para editar.');
            return copy;
        } catch (e) {
            toast.error(errorMessage(e, 'Falha ao duplicar.'));
            return null;
        }
    };

    const toggleStatus = async (reason: string) => {
        if (!template) return;
        try {
            const updated = await communicationApi.toggleTemplateStatus(template.id, reason);
            setTemplate(sync({ ...template, status: updated.status }));
            toast.success(updated.status === 'ACTIVE' ? 'Modelo ativado.' : 'Modelo desativado.');
        } catch (e) {
            toast.error(errorMessage(e, 'Falha ao alterar o status.'));
        }
    };

    const linkToComposition = async (target: MessageComposition): Promise<boolean> => {
        if (!template) return false;
        try {
            await communicationApi.linkTemplate(template, target.id, target.templateIds.length);
            toast.success('Modelo vinculado à composição.');
            return true;
        } catch (e) {
            toast.error(errorMessage(e, 'Falha ao vincular.'));
            return false;
        }
    };

    const compatibleCompositions = template
        ? compositions.filter((c) => {
              if (c.channel !== template.channel || c.templateIds.includes(template.id)) return false;
              const hasEnvelope = allTemplates.some((t) => c.templateIds.includes(t.id) && isEnvelope(t));
              return !isEnvelope(template) || !hasEnvelope;
          })
        : [];

    const config = emptyChannelConfig(template?.channel ?? 'EMAIL');

    return {
        loading,
        saving,
        dirty,
        apiVersion,
        providerCatalog,
        template,
        composition,
        linked,
        readOnly,
        compatibleCompositions,
        unused: template ? unusedVariableNames(template, config) : [],
        constantNames: template ? usedConstantNames(template, config) : [],
        start,
        update,
        save,
        duplicate,
        toggleStatus,
        linkToComposition,
        reload: load,
    };
};

export type TemplateEditor = ReturnType<typeof useTemplateEditor>;
